<template>
    <AdminLayout>
        <div class="clients-container">
            <!-- Header with search and filters -->
            <div class="clients-header">
                <h2>Client Management</h2>
                <div class="search-filters">
                    <input type="text" v-model="searchModel.fullname_client" placeholder="Search clients..."
                        class="search-input" @keyup.enter="search">
                    <select v-model="searchModel.status" class="filter-select">
                        <option value="">All Status</option>
                        <option value="online">Online Only</option>
                        <option value="offline">Offline Only</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                    <button @click="search" class="btn-search">
                        <span>🔍</span> Search
                    </button>
                </div>
            </div>

            <!-- Stats Cards -->
            <div class="stats-cards">
                <div class="stat-card">
                    <div class="stat-icon total">👥</div>
                    <div class="stat-info">
                        <span class="stat-label">Total Clients</span>
                        <span class="stat-value">{{ clients.length }}</span>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon online">🟢</div>
                    <div class="stat-info">
                        <span class="stat-label">Online</span>
                        <span class="stat-value">{{ onlineCount }}</span>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon offline">🔴</div>
                    <div class="stat-info">
                        <span class="stat-label">Offline</span>
                        <span class="stat-value">{{ offlineCount }}</span>
                    </div>
                </div>
                <div class="stat-card">
                    <div class="stat-icon active">✅</div>
                    <div class="stat-info">
                        <span class="stat-label">Active</span>
                        <span class="stat-value">{{ activeCount }}</span>
                    </div>
                </div>
            </div>

            <!-- Clients Table -->
            <div class="table-wrapper">
                <table class="clients-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Full Name</th>
                            <th>Status</th>
                            <th>Account Status</th>
                            <th>Disconnected At</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="isSearching">
                            <TableSkeleton :columns="12" />
                        </template>
                        <template v-else-if="clients?.length === 0">
                            <EmptyTable :colspan="12" />
                        </template>
                        <template v-else>
                            <tr v-for="client in clients" :key="client.syspk_client" class="client-row">
                                <td class="username-cell">
                                    <span class="username">{{ client.username_client }}</span>
                                </td>

                                <td class="name-cell">
                                    <span class="fullname">{{ client.fullname_client }}</span>
                                </td>

                                <td>
                                    <span :class="['status-badge', client.isonline_client ? 'online' : 'offline']">
                                        <span class="status-dot"></span>
                                        {{ client.isonline_client ? 'Online' : 'Offline' }}
                                    </span>
                                </td>

                                <td>
                                    <span :class="['status-badge', client.isactive_client ? 'active' : 'inactive']">
                                        {{ client.isactive_client ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>

                                <td class="date-cell">
                                    <span class="date">{{ formatDate(client.date_ad) }}</span>
                                    <span class="time">{{ formatTime(client.date_ad) }}</span>
                                </td>

                                <td>
                                    <button @click="handleClientClick(client.sysfk_client_ad)" class="btn-view">
                                        <span>👁️</span> View
                                    </button>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>

            <!-- Pagination (if needed) -->
            <div class="pagination" v-if="clients.length > 0">
                <button class="page-btn" :disabled="currentPage === 1">←</button>
                <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
                <button class="page-btn" :disabled="currentPage === totalPages">→</button>
            </div>
        </div>
    </AdminLayout>

    <FullscreenModal :show="showClientInfoModal" @close="toggleClientInfoModal">
        <ClientInfo ref="clientInfoRef" />
    </FullscreenModal>
</template>

<script setup>
import EmptyTable from '@/components/animations/EmptyTable.vue';
import TableSkeleton from '@/components/animations/TableSkeleton.vue';
import ClientInfo from '@/components/infos/ClientInfo.vue';
import FullscreenModal from '@/components/modals/FullscreenModal.vue';
import AdminLayout from '@/layouts/AdminLayout.vue';
import clientService from '@/services/clientService';
import { onMounted, ref, computed } from 'vue';
const isSearching = ref(false);
const clients = ref([]);
const searchModel = ref({
    fullname_client: '',
    status: ''
});
const currentPage = ref(1);
const itemsPerPage = ref(20);

// Computed stats
const onlineCount = computed(() => {
    return clients.value.filter(c => c.isonline_client === 1 || c.isonline_client === true).length;
});

const offlineCount = computed(() => {
    return clients.value.filter(c => c.isonline_client === 0 || c.isonline_client === false).length;
});

const activeCount = computed(() => {
    return clients.value.filter(c => c.isactive_client === 1 || c.isactive_client === true).length;
});

const totalPages = computed(() => {
    return Math.ceil(clients.value.length / itemsPerPage.value);
});

// Format date functions
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });
}



const search = async () => {
    try {
        isSearching.value = true;
        clients.value = await clientService.getRecentlyDisconnected(searchModel.value);
    } catch (error) {
        console.error('Error fetching clients:', error);
        // You might want to show a toast notification here
    }
    finally{
        isSearching.value = false;
    }
}

const showClientInfoModal = ref(false);
const clientInfoRef = ref(null);

const toggleClientInfoModal = () => {
    showClientInfoModal.value = !showClientInfoModal.value;
}

const handleClientClick = (id) => {
    toggleClientInfoModal();
    if (clientInfoRef.value) {
        clientInfoRef.value.getClientInfo(id);
    }
}

onMounted(() => {
    search();
})
</script>

<style scoped>
.clients-container {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
}

/* Header Styles */
.clients-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.clients-header h2 {
    font-size: 24px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.search-filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.search-input {
    padding: 10px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    width: 250px;
    transition: all 0.3s ease;
}

.search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-select {
    padding: 10px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background: white;
    cursor: pointer;
}

.btn-search {
    padding: 10px 20px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-search:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

/* Stats Cards */
.stats-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.stat-card {
    background: white;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    transition: transform 0.2s ease;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
}

.stat-icon.total {
    background: #e0f2fe;
    color: #0284c7;
}

.stat-icon.online {
    background: #dcfce7;
    color: #059669;
}

.stat-icon.offline {
    background: #fee2e2;
    color: #dc2626;
}

.stat-icon.active {
    background: #dbeafe;
    color: #2563eb;
}

.stat-info {
    display: flex;
    flex-direction: column;
}

.stat-label {
    font-size: 12px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stat-value {
    font-size: 24px;
    font-weight: 600;
    color: #1e293b;
}

/* Table Styles */
.table-wrapper {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.clients-table {
    width: 100%;
    border-collapse: collapse;
}

.clients-table th {
    background: #f8fafc;
    padding: 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e2e8f0;
}

.clients-table td {
    padding: 16px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
}

.client-row {
    transition: background-color 0.2s ease;
    cursor: pointer;
}

.client-row:hover {
    background-color: #f8fafc;
}

.username {
    font-weight: 500;
    color: #1e293b;
}

.fullname {
    color: #475569;
}

/* Status Badges */
.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
}

.status-badge.online {
    background: #dcfce7;
    color: #059669;
}

.status-badge.offline {
    background: #fee2e2;
    color: #dc2626;
}

.status-badge.active {
    background: #dbeafe;
    color: #2563eb;
}

.status-badge.inactive {
    background: #f1f5f9;
    color: #64748b;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.status-badge.online .status-dot {
    background: #059669;
    box-shadow: 0 0 0 2px #a7f3d0;
}

.status-badge.offline .status-dot {
    background: #dc2626;
    box-shadow: 0 0 0 2px #fecaca;
}

/* Date Cell */
.date-cell {
    display: flex;
    flex-direction: column;
}

.date {
    font-size: 13px;
    color: #1e293b;
}

.time {
    font-size: 11px;
    color: #94a3b8;
}

/* Action Button */
.btn-view {
    padding: 6px 12px;
    background: #f1f5f9;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-view:hover {
    background: #3b82f6;
    color: white;
    transform: translateY(-1px);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 48px !important;
}

.empty-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.empty-icon {
    font-size: 48px;
    opacity: 0.5;
}

.empty-message p {
    color: #94a3b8;
    font-size: 14px;
    margin: 0;
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
}

.page-btn {
    padding: 8px 16px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: #64748b;
}

/* Responsive Design */
@media (max-width: 768px) {
    .clients-container {
        padding: 16px;
    }

    .clients-header {
        flex-direction: column;
        align-items: stretch;
    }

    .search-filters {
        flex-direction: column;
    }

    .search-input,
    .filter-select,
    .btn-search {
        width: 100%;
    }

    .stats-cards {
        grid-template-columns: 1fr 1fr;
    }

    .clients-table {
        font-size: 13px;
    }

    .clients-table th,
    .clients-table td {
        padding: 12px 8px;
    }

    .status-badge {
        padding: 4px 8px;
        font-size: 11px;
    }
}

@media (max-width: 480px) {
    .stats-cards {
        grid-template-columns: 1fr;
    }
}
</style>